import { AlertTriangle, CalendarClock, CheckCircle2, RefreshCw, Swords, Trophy, UserRound, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDivisionLabel } from '../online/league';
import {
  getOnlineTournamentBracket,
  getOnlineTournamentState,
  joinOnlineTournament,
  listOnlineTournaments,
  withdrawOnlineTournament,
  type OnlineTournamentListing,
  type OnlineTournamentMatch,
  type OnlineTournamentPersonalState,
} from '../services/onlineLeague';

interface OnlineTournamentsPanelProps {
  seasonId?: string;
  hasProfile: boolean;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

function roundLabel(round: number, bracketSize: number) {
  const rounds = Math.log2(bracketSize);
  if (round === rounds) return 'Final';
  if (round === rounds - 1) return 'Semifinal';
  if (round === rounds - 2) return 'Cuartos';
  return `Ronda ${round}`;
}

function statusLabel(status: OnlineTournamentListing['status']) {
  return ({ DRAFT: 'Borrador', REGISTRATION: 'Inscripción abierta', IN_PROGRESS: 'En juego', COMPLETED: 'Finalizado', CANCELLED: 'Cancelado' })[status];
}

function eligibility(tournament: OnlineTournamentListing) {
  const min = tournament.minimumDivision ? getDivisionLabel(tournament.minimumDivision) : null;
  const max = tournament.maximumDivision ? getDivisionLabel(tournament.maximumDivision) : null;
  if (!min && !max) return 'Abierto a todas las divisiones';
  if (min === max) return `Solo ${min}`;
  return `${min ?? 'Provincial'} — ${max ?? 'Mundial'}`;
}

export function OnlineTournamentsPanel({ seasonId, hasProfile }: OnlineTournamentsPanelProps) {
  const [tournaments, setTournaments] = useState<OnlineTournamentListing[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [bracket, setBracket] = useState<OnlineTournamentMatch[]>([]);
  const [personal, setPersonal] = useState<OnlineTournamentPersonalState>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listOnlineTournaments(seasonId);
      setTournaments(list);
      setSelectedId((current) => list.some((tournament) => tournament.id === current) ? current : list[0]?.id ?? null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se han podido cargar los torneos.');
    } finally {
      setLoading(false);
    }
  }, [seasonId]);

  useEffect(() => { void loadTournaments(); }, [loadTournaments]);

  const loadSelection = useCallback(async () => {
    if (!selectedId) {
      setBracket([]);
      setPersonal({});
      return;
    }
    try {
      const [nextBracket, nextPersonal] = await Promise.all([
        getOnlineTournamentBracket(selectedId),
        hasProfile ? getOnlineTournamentState(selectedId) : Promise.resolve({}),
      ]);
      setBracket(nextBracket);
      setPersonal(nextPersonal);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se ha podido cargar el cuadro.');
    }
  }, [hasProfile, selectedId]);

  useEffect(() => { void loadSelection(); }, [loadSelection]);

  const selected = tournaments.find((tournament) => tournament.id === selectedId) ?? null;
  const rounds = useMemo(() => {
    const grouped = new Map<number, OnlineTournamentMatch[]>();
    bracket.forEach((match) => grouped.set(match.roundNumber, [...(grouped.get(match.roundNumber) ?? []), match]));
    return [...grouped.entries()].sort(([left], [right]) => left - right);
  }, [bracket]);

  const refresh = async () => {
    await loadTournaments();
    await loadSelection();
  };
  const changeEntry = async (action: () => Promise<void>) => {
    setSaving(true);
    setError(null);
    try {
      await action();
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se ha podido actualizar la inscripción.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <section className="online-tournaments-loading card"><RefreshCw className="spin" size={18} /> Cargando torneos oficiales…</section>;

  return <section className="online-tournaments">
    <header className="online-tournaments-header">
      <div><span className="card-label"><Trophy size={15} /> COPAS ONLINE</span><h2>Eliminatorias oficiales</h2><p>La inscripción congela tu plantilla. Los partidos se resuelven en el servidor y la copa nunca modifica la clasificación de liga.</p></div>
      <button className="ghost-action" onClick={() => void refresh()} disabled={saving}><RefreshCw size={14} /> Actualizar</button>
    </header>

    {error && <div className="online-message online-message--error" role="alert"><AlertTriangle size={17} /><span>{error}</span></div>}

    {!tournaments.length && <section className="online-empty card"><CalendarClock size={26} /><h2>Sin torneos publicados</h2><p>Cuando se abra una copa de esta temporada aparecerá aquí con su ventana de inscripción y sus requisitos.</p></section>}

    {!!tournaments.length && <div className="online-tournament-layout">
      <aside className="online-tournament-list" aria-label="Torneos disponibles">
        {tournaments.map((tournament) => <button key={tournament.id} className={tournament.id === selectedId ? 'active' : ''} onClick={() => setSelectedId(tournament.id)}>
          <span><Trophy size={15} /><small>{statusLabel(tournament.status)}</small></span><b>{tournament.name}</b><em>{tournament.participantCount}/{tournament.bracketSize} participantes</em>
        </button>)}
      </aside>
      {selected && <div className="online-tournament-detail">
        <section className="online-tournament-overview card">
          <div><span className="card-label"><Swords size={15} /> {selected.bracketSize} PARTICIPANTES · ELIMINACIÓN DIRECTA</span><h3>{selected.name}</h3><p>{eligibility(selected)} · cierre de inscripción {formatDate(selected.registrationClosesAt)}</p></div>
          <div className={`online-tournament-state online-tournament-state--${selected.status.toLowerCase()}`}><b>{statusLabel(selected.status)}</b><small>{selected.status === 'COMPLETED' && selected.winnerAlias ? `Campeón: ${selected.winnerAlias}` : `Comienza ${formatDate(selected.startsAt)}`}</small></div>
        </section>

        <section className="online-tournament-entry card">
          <span><UserRound size={18} /></span><div><b>{personal.entryStatus ? `Tu estado: ${personal.entryStatus === 'CONFIRMED' ? 'clasificado al cuadro' : personal.entryStatus.toLowerCase()}` : 'Aún no estás inscrito'}</b><small>{personal.seedPosition ? `Posición de sorteo ${personal.seedPosition}` : 'La inscripción requiere una plantilla online validada.'}</small></div>
          {selected.status === 'REGISTRATION' && hasProfile && !personal.entryStatus && <button className="primary-action" disabled={saving} onClick={() => void changeEntry(() => joinOnlineTournament(selected.id))}>{saving ? <RefreshCw className="spin" size={14} /> : <CheckCircle2 size={14} />}{saving ? 'Inscribiendo…' : 'Inscribirme'}</button>}
          {selected.status === 'REGISTRATION' && personal.entryStatus === 'REGISTERED' && <button className="ghost-action" disabled={saving} onClick={() => void changeEntry(() => withdrawOnlineTournament(selected.id))}><XCircle size={14} /> Retirarme</button>}
          {!hasProfile && selected.status === 'REGISTRATION' && <small className="online-entry-note">Vincula tu plantilla en Liga Online para participar.</small>}
        </section>

        {selected.status !== 'REGISTRATION' && <section className="online-tournament-bracket card" aria-label={`Cuadro de ${selected.name}`}>
          <header><div><span className="card-label"><Swords size={15} /> CUADRO DINÁMICO</span><h3>Camino al título</h3></div><small>{selected.status === 'IN_PROGRESS' ? 'El worker avanza el cuadro tras cada resultado verificado.' : 'Resultados verificados y cuadro inmutable.'}</small></header>
          {!rounds.length && <div className="online-bracket-waiting"><RefreshCw className="spin" size={16} /> Preparando los emparejamientos autoritativos…</div>}
          {!!rounds.length && <div className="online-bracket-scroll"><div className="online-bracket-board">
            {rounds.map(([round, matches]) => <section className="online-bracket-round" key={round}><header><small>RONDA {round}</small><b>{roundLabel(round, selected.bracketSize)}</b></header><div>{matches.map((match) => <TournamentMatchCard key={match.id} match={match} />)}</div></section>)}
          </div></div>}
        </section>}
      </div>}
    </div>}
  </section>;
}

function TournamentMatchCard({ match }: { match: OnlineTournamentMatch }) {
  const played = match.homeGoals !== null && match.awayGoals !== null;
  const score = played ? `${match.homeGoals} — ${match.awayGoals}` : 'VS';
  const shootout = match.resolution === 'DETERMINISTIC_PENALTIES'
    ? `Pen. ${match.homeShootoutGoals}–${match.awayShootoutGoals}` : null;
  return <article className={`online-bracket-match online-bracket-match--${match.status.toLowerCase()}`}>
    <div className={match.winnerPublicPlayerId === match.homePublicPlayerId ? 'winner' : ''}><span>{match.homeAlias ?? 'Por definir'}</span><b>{played ? match.homeGoals : '—'}</b></div>
    <div className={match.winnerPublicPlayerId === match.awayPublicPlayerId ? 'winner' : ''}><span>{match.awayAlias ?? 'Por definir'}</span><b>{played ? match.awayGoals : '—'}</b></div>
    <footer><small>{shootout ?? (match.status === 'SIMULATING' ? 'Simulando' : score)}</small></footer>
  </article>;
}
